import { Page, View, Document } from "@react-pdf/renderer";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { DEFAULT_FONT_COLOR, setSettings } from "lib/redux/settingsSlice";
import type { Settings, ShowForm } from "lib/redux/settingsSlice";
import type { Resume } from "lib/redux/types";
import { SuppressResumePDFErrorMessage } from "components/Resume/ResumePDF/common/SuppressResumePDFErrorMessage";
import { WorkExperience } from "./WorkExperience";
import { Education } from "./Education";
import { Project } from "./Project";
import { Skills } from "./Skills";
import { Certificate } from "./Certificate";
import { Language } from "./Language";
import { Award } from "./Award";
import { Custom } from "./Custom";
import { Summary } from "./Summary";
import { Profile } from "./Profile";
import { A4_HEIGHT_PT, LETTER_HEIGHT_PT } from "lib/constants";
import { JSX } from "react";
import { FontSizeProvider } from "components/Resume/ResumePDF/FontSizeContext";

export const TemplateEight = ({
  resume,
  settings,
  isPDF = false,
}: {
  resume: Resume;
  settings: Settings;
  isPDF?: boolean;
}) => {
  const {
    profile,
    workExperiences,
    educations,
    projects,
    skills,
    certifications,
    languages,
    awards,
    custom,
  } = resume;
  const { name } = profile;
  const {
    fontFamily,
    fontSize,
    documentSize,
    formToHeading,
    formToShow,
    formsOrder,
  } = settings;
  const themeColor = settings.themeColor || DEFAULT_FONT_COLOR;
  const leftRightFormOrder = settings.leftRightFormOrder;

  const showFormsOrder = formsOrder.filter((form) => formToShow[form]);
  const leftShowFormsOrder = leftRightFormOrder?.left?.filter(
    (form) => formToShow[form]
  );
  const rightShowFormsOrder = leftRightFormOrder?.right?.filter(
    (form) => formToShow[form]
  );

  const formTypeToComponent: { [type in ShowForm]: () => JSX.Element } = {
    workExperiences: () => (
      <WorkExperience
        heading={formToHeading["workExperiences"]}
        workExperiences={workExperiences}
        themeColor={themeColor}
      />
    ),
    educations: () => (
      <Education
        heading={formToHeading["educations"]}
        educations={educations}
        themeColor={themeColor}
      />
    ),
    projects: () => (
      <Project
        heading={formToHeading["projects"]}
        projects={projects}
        themeColor={themeColor}
        isPDF={isPDF}
      />
    ),
    skills: () => (
      <Skills
        heading={formToHeading["skills"]}
        skills={skills}
        themeColor={themeColor}
      />
    ),
    certifications: () => (
      <Certificate
        heading={formToHeading["certifications"]}
        certifications={certifications}
        themeColor={themeColor}
        isPDF={isPDF}
      />
    ),
    languages: () => (
      <Language
        heading={formToHeading["languages"]}
        languages={languages}
        themeColor={themeColor}
        isPDF={isPDF}
      />
    ),
    awards: () => (
      <Award
        heading={formToHeading["awards"]}
        awards={awards}
        themeColor={themeColor}
        isPDF={isPDF}
      />
    ),
    custom: () => (
      <Custom
        heading={formToHeading["custom"]}
        custom={custom}
        themeColor={themeColor}
      />
    ),
  };

  return (
    <>
      <Document title={`${name} Resume`} author={name} producer={"OpenResume"}>
        <Page
          size={documentSize === "A4" ? "A4" : "LETTER"}
          style={{
            ...styles.flexCol,
            color: DEFAULT_FONT_COLOR,
            fontFamily,
            fontSize: fontSize + "pt",
            height:
              documentSize === "A4"
                ? `${A4_HEIGHT_PT}pt`
                : `${LETTER_HEIGHT_PT}pt`,
          }}
        >
          <FontSizeProvider fontSize={fontSize}>
            <View style={{ ...styles.flexRow, height: `${spacing[5]}` }} fixed>
              <View
                style={{
                  width: "35%",
                  backgroundColor: themeColor,
                  paddingLeft: spacing[10],
                  paddingRight: spacing[10],
                }}
              ></View>
              <View
                style={{
                  width: "65%",
                  paddingLeft: spacing[10],
                  paddingRight: spacing[10],
                }}
              ></View>
            </View>
            <View
              style={{
                ...styles.flexRow,
                marginTop: `-${spacing[0.5]}`,
              }}
            >
              <View
                style={{
                  backgroundColor: themeColor,
                  width: "35%",
                  padding: `${spacing[10]} ${spacing[10]} ${spacing[0]} ${spacing[10]}`,
                }}
              >
                <Profile
                  profile={profile}
                  themeColor={themeColor}
                  isPDF={isPDF}
                />
                {profile.summary && (
                  <Summary
                    heading="PROFILE"
                    summary={profile.summary}
                    themeColor="white"
                  />
                )}
                {leftShowFormsOrder?.map((form) => {
                  const Component = formTypeToComponent[form];
                  return <Component key={form} />;
                })}
              </View>
              <View
                style={{
                  width: "65%",
                  padding: `${spacing[10]} ${spacing[10]} ${spacing[0]} ${spacing[10]}`,
                }}
              >
                {rightShowFormsOrder?.map((form) => {
                  const Component = formTypeToComponent[form];
                  return <Component key={form} />;
                })}
              </View>
            </View>
            <View
              style={{
                flex: 1,
                width: "100%",
                ...styles.flexRow,
                marginTop: `-${spacing[0.5]}`,
              }}
            >
              <View
                style={{
                  width: "35%",
                  backgroundColor: themeColor,
                  paddingLeft: spacing[10],
                  paddingRight: spacing[10],
                }}
              ></View>
              <View
                style={{
                  width: "65%",
                  paddingLeft: spacing[10],
                  paddingRight: spacing[10],
                }}
              ></View>
            </View>
            <View
              style={{
                ...styles.flexRow,
                height: `${spacing[5]}`,
                marginTop: `-${spacing[0.5]}`,
              }}
              fixed
            >
              <View
                style={{
                  width: "35%",
                  backgroundColor: themeColor,
                  paddingLeft: spacing[10],
                  paddingRight: spacing[10],
                }}
              ></View>
              <View
                style={{
                  width: "65%",
                  paddingLeft: spacing[10],
                  paddingRight: spacing[10],
                }}
              ></View>
            </View>
          </FontSizeProvider>
        </Page>
      </Document>
      <SuppressResumePDFErrorMessage />
    </>
  );
};
